import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Typography,
  Box,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  Folder as FolderIcon,
  Cloud as CloudIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useDrag, useDrop } from 'react-dnd';

import { Group, DashboardLayout, DashboardTheme } from '../../types';
import { useDashboard } from '../../hooks/useDashboard';
import { useDragDrop } from '../../contexts/DragDropContext';
import TileCard from '../Tile/TileCard';
import IconDisplay from '../IconDisplay/IconDisplay';
import { useTheme } from '@mui/material/styles';

interface GroupCardProps {
  group: Group;
  editMode: boolean;
  index: number;
  layout: DashboardLayout;
  theme: DashboardTheme;
}

const GroupCard: React.FC<GroupCardProps> = ({
  group,
  editMode,
  index,
  layout,
  theme
}) => {
  const { updateGroup, deleteGroup, setSelectedItem, config } = useDashboard();
  const { setDraggedItem, setDragType, handleGroupReorder, handleTileDrop, isDragging } = useDragDrop();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const muiTheme = useTheme();
  
  // Get theme colors
  const themeColors = config?.theme?.customColors || {} as NonNullable<DashboardTheme['customColors']>;

  // Drag functionality for groups
  const [{ isDragSource }, drag, dragPreview] = useDrag({
    type: 'GROUP',
    item: () => {
      const dragItem = {
        id: group.id,
        type: 'group' as const,
        data: group
      };
      setDraggedItem(dragItem);
      setDragType('group');
      return dragItem;
    },
    end: () => {
      setDraggedItem(null);
      setDragType(null);
    },
    collect: (monitor) => ({
      isDragSource: monitor.isDragging(),
    }),
  });

  // Drop functionality for groups (group reordering and tile drops)
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['GROUP', 'TILE'],
    drop: (draggedItem: any) => {
      if (draggedItem.type === 'group' && draggedItem.id !== group.id) {
        handleGroupReorder(draggedItem.id, group.id);
      } else if (draggedItem.type === 'tile') {
        // Drop tile into this group
        handleTileDrop(draggedItem.id, group.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  // Groups are always expanded - no collapsing functionality
  const isExpanded = true;

  // Sort tiles by order 
  const sortedTiles = [...group.tiles].sort((a, b) => a.order - b.order);
  // Show all tiles that are marked as visible (ignore group collapse state here)
  const visibleTiles = sortedTiles.filter(tile => tile.isVisible);


  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setSelectedItem({ type: 'group', id: group.id });
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete the "${group.name}" group? This will also delete all tiles in this group.`)) {
      await deleteGroup(group.id);
    }
    handleMenuClose();
  };

  const handleAddTile = () => {
    const event = new CustomEvent('dashboard:new-tile', { 
      detail: { groupId: group.id } 
    });
    document.dispatchEvent(event);
  };


  // Remove collapse functionality - groups are always expanded

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        if (node) {
          dragPreview(node);
          drop(node);
        }
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mb: 4,
        mx: (layout.groupHorizontalMargin || 8) / 8, // Convert px to MUI spacing units
        transform: isDragSource ? 'scale(0.98) rotate(1deg)' : 'none',
        opacity: isDragSource ? 0.8 : 1,
        zIndex: isDragSource ? 1000 : 'auto',
        // Drop feedback
        ...(isOver && canDrop && {
          backgroundColor: 'primary.50',
          borderRadius: 2,
          p: 2,
        }),
      }}
    >
      {/* Group Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          px: 8,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: themeColors.groupIconBackground || group.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <IconDisplay
            icon={group.icon || 'Cloud'}
            iconType={group.iconType}
            iconUrl={group.iconUrl}
            size={20}
            fallbackIcon="Cloud"
            sx={{ color: 'white' }}
          />
        </Box>
        
        <Typography 
          variant="h4" 
          component="h2" 
          sx={{ 
            fontWeight: 700,
            color: themeColors.groupTitle || 'text.primary',
            flexGrow: 1,
            fontSize: '1.5rem',
          }}
        >
          {group.name}
        </Typography>
        
        {editMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              ref={drag}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                borderRadius: 1,
                color: 'text.disabled',
                cursor: 'grab',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: themeColors.editButton || '#3f3f3e',
                  backgroundColor: 'rgba(63, 63, 62, 0.1)',
                },
                '&:active': { 
                  cursor: 'grabbing',
                  backgroundColor: 'action.selected',
                }
              }}
            >
              <DragIcon sx={{ fontSize: 16 }} />
            </Box>
            
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              aria-label="group options"
              sx={{
                p: 1,
                borderRadius: 1.5,
                transition: 'all 0.2s ease',
                color: themeColors.editButton || '#3f3f3e',
                '&:hover': {
                  backgroundColor: 'rgba(63, 63, 62, 0.1)',
                  color: themeColors.editButton || '#3f3f3e',
                }
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Group Content */}
      <Box sx={{ mb: 2, px: (layout.groupHorizontalPadding || 16) / 8 }}>
          {visibleTiles.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 2,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 500 }}
              >
                No tiles in this group
              </Typography>
              {editMode && (
                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                  Click "Add Tile" to get started
                </Typography>
              )}
            </Box>
          ) : (
            <Grid 
              container 
              spacing={Math.max(1, (layout.tileSpacing || 16) / 8)} // Convert px to MUI spacing units
              sx={{ mt: -1 }}
            >
              {visibleTiles.map((tile, tileIndex) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6}
                  md={4}
                  key={tile.id}
                >
                  <TileCard
                    tile={tile}
                    editMode={editMode}
                    index={tileIndex}
                    groupId={group.id}
                  />
                </Grid>
              ))}
            </Grid>
          )}
      </Box>

      {/* Group Actions */}
      {editMode && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <IconButton
            onClick={handleAddTile}
            size="small"
            sx={{
              border: '2px dashed',
              borderColor: themeColors.addButton || '#6b7280',
              borderRadius: 2,
              p: 1.5,
              color: themeColors.addButton || '#6b7280',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                borderStyle: 'solid',
                borderColor: themeColors.addButton || '#6b7280',
                transform: 'scale(1.05)',
              },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Group</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleAddTile}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Tile</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Group</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GroupCard;